/**
 * SessionAIPanel - AI features for individual sessions
 * - Generate summaries
 * - Q&A chatbot
 * - Audio transcription
 */
import React, { useState, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Wand2, MessageSquare, Mic, Send, Loader2, FileAudio, Copy, RefreshCw, Sparkles } from 'lucide-react';
import { aiAPI, getErrorMessage } from '@/lib/api';

const SessionAIPanel = ({ eventId, session }) => {
  const [question, setQuestion] = useState('');
  const [qaHistory, setQaHistory] = useState([]);
  const [additionalContent, setAdditionalContent] = useState('');
  const fileInputRef = useRef(null);

  // Fetch existing transcript/summary
  const { data: transcriptData } = useQuery({
    queryKey: ['sessionTranscript', eventId, session.id],
    queryFn: () => aiAPI.getTranscript(eventId, session.id).then(r => r.data),
    enabled: !!session.id,
  });

  // Generate Summary mutation
  const summaryMutation = useMutation({
    mutationFn: (data) => aiAPI.generateSummary(eventId, session.id, data),
    onSuccess: (res) => {
      toast.success('Summary generated!');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to generate summary')),
  });

  // Q&A mutation
  const qaMutation = useMutation({
    mutationFn: (data) => aiAPI.askQuestion(eventId, data),
    onSuccess: (res) => {
      setQaHistory(prev => [...prev, {
        question,
        answer: res.data.answer,
        timestamp: new Date().toLocaleTimeString(),
      }]);
      setQuestion('');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to process question')),
  });

  // Transcription mutation
  const transcribeMutation = useMutation({
    mutationFn: (formData) => aiAPI.transcribe(eventId, session.id, formData),
    onSuccess: (res) => {
      toast.success('Audio transcribed!');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Transcription failed')),
  });

  const handleGenerateSummary = () => {
    summaryMutation.mutate({ content: additionalContent, regenerate: true });
  };

  const handleAskQuestion = () => {
    if (!question.trim()) return;
    qaMutation.mutate({
      question,
      session_id: session.id,
      context: transcriptData?.transcript || '',
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (25MB max)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 25MB');
      return;
    }

    const formData = new FormData();
    formData.append('audio', file);
    transcribeMutation.mutate(formData);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <Card className="h-full" data-testid="session-ai-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="h-5 w-5 text-purple-600" />
          AI Assistant
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="summary">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3">
            <TabsTrigger value="summary"><Wand2 className="h-4 w-4 mr-1" />Summary</TabsTrigger>
            <TabsTrigger value="qa"><MessageSquare className="h-4 w-4 mr-1" />Q&A</TabsTrigger>
            <TabsTrigger value="transcribe"><Mic className="h-4 w-4 mr-1" />Transcribe</TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4 mt-4">
            {transcriptData?.summary || summaryMutation.data?.data?.summary ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="bg-purple-50">AI Generated</Badge>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(summaryMutation.data?.data?.summary || transcriptData?.summary)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleGenerateSummary}
                      disabled={summaryMutation.isPending}
                    >
                      <RefreshCw className={`h-4 w-4 ${summaryMutation.isPending ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-64 rounded-md border p-4">
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                    {summaryMutation.data?.data?.summary || transcriptData?.summary}
                  </div>
                </ScrollArea>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-500">
                  Generate an AI summary of this session. You can optionally add notes or transcript below.
                </p>
                <Textarea
                  placeholder="Add session notes, transcript, or key points to include in the summary..."
                  value={additionalContent}
                  onChange={(e) => setAdditionalContent(e.target.value)}
                  rows={4}
                />
                <Button 
                  onClick={handleGenerateSummary} 
                  disabled={summaryMutation.isPending}
                  className="w-full"
                  data-testid="generate-summary-btn"
                >
                  {summaryMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                  ) : (
                    <><Wand2 className="h-4 w-4 mr-2" />Generate Summary</>
                  )}
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Q&A Tab */}
          <TabsContent value="qa" className="space-y-4 mt-4">
            <ScrollArea className="h-48 rounded-md border p-3">
              {qaHistory.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">
                  Ask questions about this session
                </p>
              ) : (
                <div className="space-y-4">
                  {qaHistory.map((item, i) => (
                    <div key={i} className="space-y-2">
                      <div className="flex gap-2">
                        <Badge variant="outline">Q</Badge>
                        <span className="text-sm">{item.question}</span>
                      </div>
                      <div className="flex gap-2 ml-4 bg-slate-50 rounded-lg p-3">
                        <Badge className="bg-purple-600">A</Badge>
                        <span className="text-sm">{item.answer}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            <div className="flex gap-2">
              <Input
                placeholder="Ask a question about this session..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAskQuestion()}
                data-testid="qa-input"
              />
              <Button 
                onClick={handleAskQuestion} 
                disabled={!question.trim() || qaMutation.isPending}
                data-testid="ask-btn"
              >
                {qaMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Transcription Tab */}
          <TabsContent value="transcribe" className="space-y-4 mt-4">
            {transcriptData?.transcript ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="bg-green-50">Transcript</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(transcriptData.transcript)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <ScrollArea className="h-40 rounded-md border p-4">
                  <div className="text-sm whitespace-pre-wrap">
                    {transcriptData.transcript}
                  </div>
                </ScrollArea>
              </div>
            ) : (
              <p className="text-sm text-slate-500">No transcript yet. Upload audio to generate.</p>
            )}

            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                onChange={handleFileUpload}
                className="hidden"
              />
              <FileAudio className="h-8 w-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-500 mb-2">
                Upload audio file (mp3, wav, m4a, etc.)
              </p>
              <p className="text-xs text-slate-400 mb-3">Max 25MB</p>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={transcribeMutation.isPending}
                data-testid="upload-audio-btn"
              >
                {transcribeMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Transcribing...</>
                ) : (
                  <><Mic className="h-4 w-4 mr-2" />Upload Audio</>
                )}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SessionAIPanel;
